// Client-side helper to read Google Sheet via Cloudflare Pages Function proxy.

export interface SheetClientEntry {
  sheetName: string;   // column A
  clientName: string;  // column B
  rememberme: string;  // column C
  centerName: string;  // column D
  centerId: string;    // column E
}

export interface SheetClient {
  name: string;
  centers: SheetClientEntry[];
}

const SHEETS_API = "/api/sheets";
// Tab "C" has NO header row — the first data row (e.g. Danagosue sl / Obrador)
// lives in A1. Reading from A2 silently dropped it from the downloadable list.
const C_RANGE = "C!A1:E10000";

export async function fetchSheetCRows(): Promise<SheetClientEntry[]> {
  // Credentials in the Sheet are operational data. Never allow the browser,
  // intermediary caches or a long-lived tab to reuse an outdated value.
  const res = await fetch(`${SHEETS_API}?range=${encodeURIComponent(C_RANGE)}&fresh=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) {
    let detail = "";
    try { detail = JSON.stringify(await res.json()); } catch { /* ignore */ }
    throw new Error(`Sheets read failed: HTTP ${res.status} ${detail}`);
  }
  const data = await res.json() as { values?: string[][] };
  const rows = data.values || [];
  return rows
    .map((r) => ({
      sheetName:  (r[0] || "").trim(),
      clientName: (r[1] || "").trim(),
      rememberme: (r[2] || "").trim(),
      centerName: (r[3] || "").trim(),
      centerId:   (r[4] || "").trim(),
    }))
    .filter((e) => e.clientName && e.centerId && e.rememberme);
}

export interface SheetNewVendor { nif: string; name: string; }
export type SheetItemKind = "producto" | "herramienta" | "otro";
export interface SheetNewItem { name: string; kind: SheetItemKind; }
export interface SheetNewArticle {
  product: string; vendor: string; code: string; format: string; price: string; article: string;
  kind: SheetItemKind;
}

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function norm(s: string): string {
  return (s || "").trim().toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "");
}

// PRODUCTO header is misspelled "PRODCUTO" in most client tabs. Accept both.
function isProductoHeader(v: string): boolean {
  const n = norm(v);
  return n === "producto" || n === "prodcuto";
}

// Center names differ between the sheet's CENTRO/Cuenta column (e.g. "Barceloneta",
// "Sidral Platja D/Aro") and the backend center descr (e.g. "BC Barceloneta",
// "Sidral Platja d'Aro"). Match leniently: drop accents and punctuation, then
// compare equal or one contained in the other. Empty wanted/row → match.
function centerKey(s: string): string {
  return norm(s).replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}
function centerMatches(rowCentro: string, wantedCenter: string): boolean {
  const a = centerKey(rowCentro), b = centerKey(wantedCenter);
  if (!a || !b) return true;
  return a === b || a.includes(b) || b.includes(a);
}

export async function fetchSheetTab(sheetName: string): Promise<string[][]> {
  const range = `'${sheetName.replace(/'/g, "''")}'!A1:Z10000`;
  const res = await fetch(`${SHEETS_API}?range=${encodeURIComponent(range)}`);
  if (!res.ok) {
    let detail = ""; try { detail = JSON.stringify(await res.json()); } catch { /* ignore */ }
    throw new Error(`Sheets tab read failed (${sheetName}): HTTP ${res.status} ${detail}`);
  }
  const data = await res.json() as { values?: string[][] };
  return data.values || [];
}

const MONTHS_NORM = MONTHS_ES.map((m) => norm(m));

export function extractNewVendors(rows: string[][], centerName: string, month: number): SheetNewVendor[] {
  if (!rows.length) return [];
  const wantedMonth = MONTHS_NORM[month - 1] || "";
  const wantedCenter = norm(centerName);

  // Find header row containing "NIF" + "PROVEEDOR" adjacent. MES/CENTRO are
  // optional siblings at +2/+3 and may be missing in single-center sheets.
  let headerRowIdx = -1;
  let nifCol = -1, provCol = -1, mesCol = -1, centroCol = -1;
  for (let r = 0; r < Math.min(rows.length, 30); r++) {
    const row = rows[r];
    if (!row) continue;
    for (let c = 0; c < row.length; c++) {
      if (norm(row[c]) === "nif" && norm(row[c + 1] || "") === "proveedor") {
        headerRowIdx = r; nifCol = c; provCol = c + 1;
        if (norm(row[c + 2] || "") === "mes") mesCol = c + 2;
        if (norm(row[c + 3] || "") === "centro") centroCol = c + 3;
        else if (norm(row[c + 2] || "") === "centro") centroCol = c + 2;
        break;
      }
    }
    if (headerRowIdx >= 0) break;
  }
  if (headerRowIdx < 0) return [];

  const out: SheetNewVendor[] = [];
  const seen = new Set<string>();
  for (let r = headerRowIdx + 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const nif = (row[nifCol] || "").trim();
    const name = (row[provCol] || "").trim();
    if (!name && !nif) continue;

    // Month resolution priority:
    //   1) explicit MES column adjacent to PROVEEDOR (if header found)
    //   2) override cell: scan only columns after provCol for a month name
    //      (sheets put per-provider override mes in M when row-level mes is
    //      different, e.g. a vendor created in Mayo on a Febrero product row)
    //   3) row-level fallback: scan all other cells for a month name
    let rowMonth = "";
    if (mesCol >= 0) rowMonth = norm(row[mesCol] || "");
    if (!rowMonth) {
      for (let c = provCol + 1; c < row.length; c++) {
        const v = norm(row[c] || "");
        if (MONTHS_NORM.includes(v)) { rowMonth = v; break; }
      }
    }
    if (!rowMonth) {
      for (let c = 0; c < row.length; c++) {
        if (c === nifCol || c === provCol) continue;
        const v = norm(row[c] || "");
        if (MONTHS_NORM.includes(v)) { rowMonth = v; break; }
      }
    }
    if (wantedMonth && rowMonth && rowMonth !== wantedMonth) continue;
    if (wantedMonth && !rowMonth) continue; // ambiguous → skip

    // Center: prefer explicit CENTRO col. Absent → single-center sheet, accept.
    if (centroCol >= 0 && wantedCenter) {
      if (!centerMatches(row[centroCol] || "", centerName)) continue;
    }

    const key = `${nif}|${name}`.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ nif, name });
  }
  return out;
}

function classifyKind(raw: string): SheetItemKind {
  const v = norm(raw);
  if (v === "producto") return "producto";
  if (v === "herramienta" || v === "herramientas") return "herramienta";
  return "otro";
}

export interface SheetProductsResult {
  products: SheetNewItem[];
  articles: SheetNewArticle[];
}

export function extractProductsAndArticles(
  rows: string[][], month: number, centerName = "",
): SheetProductsResult {
  if (!rows.length) return { products: [], articles: [] };
  const wantedMonth = MONTHS_NORM[month - 1] || "";

  // Find header row that contains the canonical product table headers.
  // We need cols: PRODUCTO, PROVEEDOR (1st), CODIGO, FORMATO, PRECIO, ARTICULO,
  // TIPO, NUEVO, [CENTRO], MES — in that approximate order. CENTRO is present
  // only in multi-center tabs (e.g. B&C).
  let hr = -1;
  const idx: Record<string, number> = {
    producto: -1, proveedor: -1, codigo: -1, formato: -1, precio: -1,
    articulo: -1, tipo: -1, nuevo: -1, centro: -1, mes: -1,
  };
  for (let r = 0; r < Math.min(rows.length, 30); r++) {
    const row = rows[r] || [];
    const found: Record<string, number> = { ...idx };
    let hits = 0;
    for (let c = 0; c < row.length; c++) {
      const v = norm(row[c]);
      // Only capture the first occurrence (article table comes before vendor list).
      if (isProductoHeader(row[c]) && found.producto < 0) { found.producto = c; hits++; }
      else if (v === "proveedor" && found.proveedor < 0) { found.proveedor = c; hits++; }
      else if (v === "codigo" && found.codigo < 0) { found.codigo = c; hits++; }
      else if (v === "formato" && found.formato < 0) { found.formato = c; hits++; }
      else if (v === "precio" && found.precio < 0) { found.precio = c; hits++; }
      else if (v === "articulo" && found.articulo < 0) { found.articulo = c; hits++; }
      else if (v === "tipo" && found.tipo < 0) { found.tipo = c; hits++; }
      else if (v === "nuevo" && found.nuevo < 0) { found.nuevo = c; hits++; }
      // Center column: "CENTRO" in multi-center tabs (B&C), "Cuenta" in others
      // (Akelarre). Both pin a row to a single cost center.
      else if ((v === "centro" || v === "cuenta") && found.centro < 0) { found.centro = c; hits++; }
      else if (v === "mes" && found.mes < 0) { found.mes = c; hits++; }
    }
    if (hits >= 6 && found.producto >= 0 && found.tipo >= 0 && found.nuevo >= 0 && found.mes >= 0) {
      hr = r; Object.assign(idx, found); break;
    }
  }
  if (hr < 0) return { products: [], articles: [] };

  const products: SheetNewItem[] = [];
  const articles: SheetNewArticle[] = [];
  const seenProd = new Set<string>();
  const seenArt = new Set<string>();

  for (let r = hr + 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const name = (row[idx.producto] || "").trim();
    if (!name) continue;
    const rowMonth = norm(row[idx.mes] || "");
    if (wantedMonth && rowMonth !== wantedMonth) continue;

    // Multi-center tabs: keep only rows for the selected center.
    if (idx.centro >= 0 && centerName && !centerMatches(row[idx.centro] || "", centerName)) continue;

    const nuevo = norm(row[idx.nuevo] || "");
    const kind = classifyKind(row[idx.tipo] || "");
    const isNew = nuevo === "si" || nuevo === "sí" || nuevo === "yes" || nuevo === "true";

    if (isNew) {
      const key = `${kind}|${name}`.toLowerCase();
      if (seenProd.has(key)) continue;
      seenProd.add(key);
      products.push({ name, kind });
    } else {
      const article = {
        product: name,
        vendor: (row[idx.proveedor] || "").trim(),
        code: (row[idx.codigo] || "").trim(),
        format: (row[idx.formato] || "").trim(),
        price: (row[idx.precio] || "").trim(),
        article: (row[idx.articulo] || "").trim(),
        kind,
      };
      const key = `${article.product}|${article.vendor}|${article.code}|${article.format}`.toLowerCase();
      if (seenArt.has(key)) continue;
      seenArt.add(key);
      // Article needs at least one of: vendor, code, format, price, article.
      if (article.vendor || article.code || article.format || article.price || article.article) {
        articles.push(article);
      }
    }
  }
  return { products, articles };
}

export function groupByClient(entries: SheetClientEntry[]): SheetClient[] {
  const map = new Map<string, SheetClient>();
  for (const e of entries) {
    let bucket = map.get(e.clientName);
    if (!bucket) {
      bucket = { name: e.clientName, centers: [] };
      map.set(e.clientName, bucket);
    }
    bucket.centers.push(e);
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "es"));
}
